import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useReactMediaRecorder } from 'react-media-recorder';
import { EventNames } from '../../../common/constants/constants';
import { RecordPreview } from '../RecordPreview/RecordPreview';
import { VideoRecordButton } from '../VideoRecordButton/VideoRecordButton';
import {
  PreviewWrapper,
  PreviewControlsWrapper,
  ButtonsWrapper,
  BackButton,
  GetReadyText,
  RecordText,
  VideoPreviewWrapper,
  VideoPreview,
  PlayIcon,
  SubmitControlsWrapper,
  RetakeButton,
  SubmitButton,
} from './RecordVideoComponent.styles';
import { RecordBackIcon } from '../../../common/assets/RecordBackIcon';
import { VideoToolStrings } from '../../../common/localization/en';
import { Counter } from './Counter/Counter';
import { RecordPlayIcon } from '../../../common/assets/RecordPlayIcon';
import { useTrackEvent } from '../../../common/hooks/useTrackEvent';
import SwitchCameraIcon from '../../../common/assets/playerIcons/SwitchCameraIcon';
import { useAppDispatch } from '../../../store/store';
import { setHideCrispChat } from '../../../store/slices/ui';
import { useCrispChat } from '../../../services/crisp/useCrispChat';

export const RECORD_TIME_LIMIT = 30;

interface RecordVideoComponentProps {
  setBlob: (val: string | undefined) => void;
  setFile: (val: File | null) => void;
  handleRemount: () => void;
  setStoryId: (val: string) => void;
  onFileChange: (file: File | null) => void;
  onRefresh: () => void;
  setIsRecordError: (error: boolean) => void;
  toggleCamera: () => void;
  camera: 'user' | 'environment';
}
const MEDIA_CREATING_TIMEOUT = 5000;

export const RecordVideoComponent: FC<RecordVideoComponentProps> = ({
  setBlob,
  handleRemount,
  setFile,
  onFileChange,
  onRefresh,
  setIsRecordError,
  toggleCamera,
  camera,
}) => {
  const { status, startRecording, stopRecording, mediaBlobUrl, previewStream } = useReactMediaRecorder({
    video: {
      facingMode: camera,
    },
    askPermissionOnMount: true,
  });

  const { trackEvent } = useTrackEvent();

  const inputRef = useRef<HTMLInputElement | null>(null);
  const previewVideoRef = useRef<HTMLVideoElement | null>(null);

  const [isCountDownStated, setIsCountDownStated] = useState(false);
  const [isPreviewVideoPlaying, setIsPreviewVideoPlaying] = useState(false);
  const [isRecorded, setIsRecorded] = useState(false);
  const [isMediaCreatingTimeout, setIsMediaCreatingTimeout] = useState(false);
  const dispatch = useAppDispatch();
  const { hideCrispChatToggle, showCrispChat } = useCrispChat();
  const [isRearCameraSupported, setIsRearCameraSupported] = useState(false);

  useEffect(() => {
    dispatch(setHideCrispChat(true));
    hideCrispChatToggle();
    return () => {
      dispatch(setHideCrispChat(false));
      showCrispChat();
    };
  }, []);

  const checkRearCamera = () => {
    if (/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
      setIsRearCameraSupported(true);
    }
  };

  const clearInput = useCallback(() => {
    if (inputRef?.current) {
      inputRef.current.value = '';
    }
  }, []);

  useEffect(() => {
    if (isRecorded) {
      setTimeout(() => {
        setIsMediaCreatingTimeout(true);
      }, MEDIA_CREATING_TIMEOUT);
    }
  }, [isRecorded]);

  const handleRecordingError = () => {
    onRefresh();
    setIsRecordError(true);
    trackEvent({ action: EventNames.RecordVideoError });
  };

  useEffect(() => {
    checkRearCamera();
  }, []);

  useEffect(() => {
    if (isMediaCreatingTimeout && status === 'stopping') {
      handleRecordingError();
    }
  }, [isMediaCreatingTimeout, status]);

  useEffect(() => {
    if (isRecorded && mediaBlobUrl && status === 'stopped') {
      setTimeout(async () => {
        const fileBlob = await fetch(mediaBlobUrl).then((res) => res.blob());
        if (fileBlob.size === 0) {
          handleRecordingError();
        }
      }, 0);
    }
  }, [isRecorded, mediaBlobUrl, status]);

  useEffect(() => {
    // to show thumbnail preview on IOS

    previewVideoRef?.current?.load();
  }, [mediaBlobUrl]);

  useEffect(() => {
    trackEvent({ action: EventNames.Record_Video_Page });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeUploadVideoHandler = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0] || null;

      if (file) {
        trackEvent({ action: EventNames.Record_Video_Page_File_Selected, size: file.size });
        clearInput();
        setFile(file);
        onFileChange(file);
        return;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clearInput, setFile],
  );

  const onBackClick = () => {
    trackEvent({ action: EventNames.Back_Button_Click });
    handleRemount();
  };

  const onStartRecording = () => {
    trackEvent({ action: EventNames.Record_Button_Click });

    setIsCountDownStated(true);
  };

  const handlePlay = () => {
    const video = previewVideoRef.current;

    if (video?.paused) {
      video.play();
    } else {
      video?.pause();
    }
  };

  const isRecording = status === 'recording';

  const recordingControls = useMemo(() => {
    return (
      <PreviewControlsWrapper>
        {isCountDownStated || isRecording ? (
          <RecordText>
            {isRecording ? VideoToolStrings.Recording : VideoToolStrings.GetReady}
          </RecordText>
        ) : (
          <GetReadyText>
            PRESS{' '}
            <Typography display="inline" color="#F00">
              REC
            </Typography>{' '}
            WHEN READY
          </GetReadyText>
        )}
        <ButtonsWrapper>
          <BackButton display="flex">
            <IconButton color="inherit" onClick={onBackClick} aria-label="back" size="large" sx={{ fontSize: '36px' }}>
              <RecordBackIcon />
            </IconButton>
          </BackButton>
          <Box>
            <VideoRecordButton
              handleStart={onStartRecording}
              handleStop={() => {
                stopRecording();
                setIsRecorded(true);
              }}
              isRecording={isRecording}
            />
          </Box>
          {isRearCameraSupported && !isRecording && (
            <Box
              onClick={() => {
                toggleCamera();
                onRefresh();
              }}
              style={{ display: 'flex', justifyContent: 'right', marginRight: '39px' }}
            >
              <SwitchCameraIcon />
            </Box>
          )}
        </ButtonsWrapper>
      </PreviewControlsWrapper>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startRecording, stopRecording, status, mediaBlobUrl, handleRemount, onChangeUploadVideoHandler]);

  return (
    <PreviewWrapper>
      {!mediaBlobUrl && (
        <>
          <Counter
            initialValue={3}
            setIsCountStarted={setIsCountDownStated}
            startRecording={startRecording}
            isCountDownStarted={isCountDownStated}
            isRecording={isRecording}
          />
          <RecordPreview stream={previewStream} />
          {recordingControls}
        </>
      )}
      {mediaBlobUrl && (
        <VideoPreviewWrapper>
          <VideoPreview
            as="video"
            src={mediaBlobUrl}
            ref={previewVideoRef}
            onClick={handlePlay}
            onPlay={() => {
              setIsPreviewVideoPlaying(true);
            }}
            onPause={() => {
              setIsPreviewVideoPlaying(false);
            }}
            onEnded={() => {
              setIsPreviewVideoPlaying(false);
            }}
            autoPlay={false}
            controls={false}
            playsInline={true}
            loop={true}
            preload="auto"
          />
          {!isPreviewVideoPlaying && (
            <PlayIcon onClick={handlePlay}>
              <RecordPlayIcon />
            </PlayIcon>
          )}
          <PreviewControlsWrapper>
            <SubmitControlsWrapper>
              <RetakeButton
                variant="text"
                onClick={() => {
                  onRefresh();
                }}
              >
                {VideoToolStrings.Retake}
              </RetakeButton>
              {isRecorded && status !== 'stopping' ? (
                <SubmitButton
                  variant="text"
                  onClick={() => {
                    setBlob(mediaBlobUrl);
                  }}
                >
                  Submit Clip
                </SubmitButton>
              ) : (
                <CircularProgress
                  sx={{
                    justifySelf: 'center',
                    color: '#ED2E38',
                  }}
                />
              )}
            </SubmitControlsWrapper>
          </PreviewControlsWrapper>
        </VideoPreviewWrapper>
      )}
    </PreviewWrapper>
  );
};
